import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import { apiFetch } from '../services/api';
import { useSocketStore } from './socket';
import { playSoundLoop, stopSound } from '../utils/alarmSounds';

export interface Alarm {
  id: string;
  userId: string;
  label: string;
  time: string; // HH:mm
  daysOfWeek: number[];
  sound: string;
  enabled: boolean;
  snoozeDuration: number;
  createdAt: string;
  updatedAt: string;
}

export interface RingingAlarm {
  alarmId: string;
  label: string;
  sound: string;
  snoozeDuration: number;
}

export const useAlarmsStore = defineStore('alarms', () => {
  const alarms = ref<Alarm[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const ringingAlarm = ref<RingingAlarm | null>(null);

  // ── Socket listener ────────────────────────────────────────────────────────
  const socketStore = useSocketStore();

  function handleAlarmRing(data: RingingAlarm) {
    ringingAlarm.value = data;
    playSoundLoop(data.sound).catch(console.error);
  }

  function _registerSocketListeners() {
    const s = socketStore.socket;
    if (!s) return;
    s.off('alarm:ring', handleAlarmRing);
    s.on('alarm:ring', handleAlarmRing);
  }

  function initSocketListeners() {
    _registerSocketListeners();
    watch(
      () => socketStore.socket,
      (s) => {
        if (s) _registerSocketListeners();
      },
    );
  }

  // ── API ────────────────────────────────────────────────────────────────────

  async function fetchAlarms() {
    loading.value = true;
    error.value = null;
    try {
      const data = await apiFetch('/alarms');
      alarms.value = data;
    } catch (__caught: unknown) {
      const err = __caught as Error;
      error.value = err.message || 'Failed to load alarms';
    } finally {
      loading.value = false;
    }
  }

  async function createAlarm(payload: Partial<Alarm>): Promise<Alarm> {
    const alarm = await apiFetch('/alarms', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    alarms.value.push(alarm);
    return alarm;
  }

  async function updateAlarm(id: string, payload: Partial<Alarm>): Promise<Alarm> {
    const alarm = await apiFetch(`/alarms/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    const idx = alarms.value.findIndex((a) => a.id === id);
    if (idx !== -1) alarms.value[idx] = alarm;
    return alarm;
  }

  async function toggleAlarm(id: string): Promise<void> {
    const alarm = await apiFetch(`/alarms/${id}/toggle`, { method: 'PATCH' });
    const idx = alarms.value.findIndex((a) => a.id === id);
    if (idx !== -1) alarms.value[idx] = alarm;
  }

  async function deleteAlarm(id: string): Promise<void> {
    await apiFetch(`/alarms/${id}`, { method: 'DELETE' });
    alarms.value = alarms.value.filter((a) => a.id !== id);
  }

  async function snoozeAlarm(alarmId: string): Promise<void> {
    stopSound();
    ringingAlarm.value = null;
    await apiFetch(`/alarms/${alarmId}/snooze`, { method: 'PATCH' });
  }

  function dismissAlarm(): void {
    stopSound();
    ringingAlarm.value = null;
  }

  return {
    alarms,
    loading,
    error,
    ringingAlarm,
    initSocketListeners,
    fetchAlarms,
    createAlarm,
    updateAlarm,
    toggleAlarm,
    deleteAlarm,
    snoozeAlarm,
    dismissAlarm,
  };
});
